import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import {
  ServicesPage,
  ServiceContent,
} from "../../../new-reports/types/newReportTypes";
import { systemGroupType } from "../../../../providers/dataPlatformReportTypes";
import { enReportServiceType } from "../seviceBuilder/helper";
import fallBackImage from "../../../reports/images/coverPageImgLongevity.png";
import ServiceViewModal from "../programming/ServiceViewModal";
import { useScoreSidePanel } from "./ScoreSidePanelContext";

type AffectedBiomarker = {
  type: "metric" | "bloodBiomarker";
  label: string;
  panelData: any;
};

export const isServiceConnectedToSystem = (
  service: ServiceContent,
  systemData: systemGroupType
): boolean => {
  const ruleSet = service.rational?.list?.[0];
  if (!ruleSet) return false;

  const metricIds = new Set(
    (systemData.breathBiomarkers || []).map((b) => b.id)
  );
  const bloodIds = new Set((systemData.bloodBiomarkers || []).map((b) => b.id));

  return ruleSet.conditions.some((condition) => {
    if (!condition.valid) return false;
    if (condition.object_name === "pnoe_metrics") {
      return condition.fields.some(
        (field) => field.valid && metricIds.has(field.name)
      );
    }
    if (condition.object_name === "lab_work") {
      return condition.fields.some(
        (field) => field.valid && bloodIds.has(field.name)
      );
    }
    return false;
  });
};

const getAffectedBiomarkers = (
  service: ServiceContent,
  systemData: systemGroupType,
  metricsMap: Map<string, any>,
  bloodMap: Map<string, any>,
  biomarkerList: any[]
): AffectedBiomarker[] => {
  const ruleSet = service.rational?.list?.[0];
  if (!ruleSet) return [];

  const metricIds = new Set(
    (systemData.breathBiomarkers || []).map((b) => b.id)
  );
  const bloodIds = new Set((systemData.bloodBiomarkers || []).map((b) => b.id));

  const results: AffectedBiomarker[] = [];

  for (const condition of ruleSet.conditions) {
    if (!condition.valid) continue;

    if (condition.object_name === "pnoe_metrics") {
      for (const field of condition.fields) {
        if (!field.valid || !metricIds.has(field.name)) continue;
        const m = metricsMap.get(field.name);
        if (!m) continue;
        results.push({
          type: "metric",
          label: m.label,
          panelData: {
            id: m.id,
            name: m.label,
            description: m.description,
            value: m.value,
            unit: m.unit,
            score: m.score,
            scoreCategory: m.scoreCategory,
            values: m.values,
            whyItIsImportantToTrackText: m.whyItIsImportantToTrackText,
            howItsMeasuredText: m.howItsMeasuredText,
            influencedBy: m.influencedBy,
            affectsThis: m.affectsThis,
          },
        });
      }
    }

    if (condition.object_name === "lab_work") {
      for (const field of condition.fields) {
        if (!field.valid || !bloodIds.has(field.name)) continue;
        const b = bloodMap.get(field.name);
        if (!b) continue;
        const bioInfo = biomarkerList?.find((bl: any) => bl.id === b.id);
        results.push({
          type: "bloodBiomarker",
          label: b.shortname || b.label,
          panelData: {
            id: b.id,
            label: b.shortname || b.label,
            fullName: b.label,
            description: bioInfo?.text || b.description,
            value: b.value,
            unit: b.unit,
            score: b.score,
            scoreCategory: b.scoreCategory,
            values: b.values,
            influencedBy: b.influencedBy,
            affectsThis: b.affectsThis,
          },
        });
      }
    }
  }

  return results;
};

const RecommendedServicesTab = () => {
  const theme = useTheme();
  const {
    openPanel,
    isOpen,
    contentType,
    metricData,
    bloodBiomarkerData,
    setHighlightedBiomarkerIds,
  } = useScoreSidePanel();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [modalService, setModalService] = useState<ServiceContent | null>(null);

  const prescriptionPlan = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.prescriptionPlan
  );

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  useEffect(() => {
    setExpandedIndex(null);
    setHighlightedBiomarkerIds(new Set());
  }, [selectedSystem]);

  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );

  const dashboard = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.dashboard
  );

  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );

  const metricsMap = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const m of dashboard?.metrics || []) {
      map.set(m.id, m);
    }
    return map;
  }, [dashboard?.metrics]);

  const bloodMap = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const b of dashboard?.bloodbiomarkers || []) {
      map.set(b.id, b);
    }
    return map;
  }, [dashboard?.bloodbiomarkers]);

  const servicesData = prescriptionPlan?.find(
    (p) => p.id === "services-page-1" && p.pageType === "services"
  ) as ServicesPage | undefined;

  const currentSystem = reportData?.systems?.find(
    (s) => s.system === selectedSystem
  );

  const filteredServices = currentSystem
    ? servicesData?.services?.filter((service) =>
        isServiceConnectedToSystem(service, currentSystem)
      )
    : servicesData?.services;

  return (
    <>
      {filteredServices && filteredServices.length > 0 ? (
        <ScrollableWrapper>
          <div style={styles.servicesListContainer}>
            {filteredServices.map((service, index) => {
              const isExpanded = expandedIndex === index;
              const affectedBiomarkers =
                isExpanded && currentSystem
                  ? getAffectedBiomarkers(
                      service,
                      currentSystem,
                      metricsMap,
                      bloodMap,
                      biomarkerList
                    )
                  : [];

              return (
                <div
                  key={index}
                  style={
                    isExpanded
                      ? styles.serviceCardExpanded(theme)
                      : styles.serviceCard(theme)
                  }
                  onClick={() => {
                    if (isExpanded) {
                      setExpandedIndex(null);
                      setHighlightedBiomarkerIds(new Set());
                    } else {
                      setExpandedIndex(index);
                      const affected = currentSystem
                        ? getAffectedBiomarkers(
                            service,
                            currentSystem,
                            metricsMap,
                            bloodMap,
                            biomarkerList
                          )
                        : [];
                      setHighlightedBiomarkerIds(
                        new Set(affected.map((a) => a.panelData.id))
                      );
                    }
                  }}
                >
                  <div style={styles.serviceCardTopRow}>
                    <img
                      src={service.imageURL || fallBackImage}
                      alt={service.serviceName}
                      style={styles.serviceCardImage}
                      onError={(e: any) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallBackImage;
                      }}
                    />
                    <div style={styles.serviceCardInfo}>
                      <p style={styles.serviceCardName(theme)}>
                        {service.serviceName}
                      </p>
                      <p style={styles.serviceCardType(theme)}>
                        {service.serviceType ||
                          enReportServiceType[service.serviceTypeCode]}
                      </p>
                    </div>
                    {isExpanded && (
                      <span
                        style={styles.viewDetailsLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalService(service);
                        }}
                      >
                        View details
                      </span>
                    )}
                  </div>
                  {isExpanded && affectedBiomarkers.length > 0 && (
                    <div style={styles.affectsRow}>
                      <span style={styles.affectsLabel}>Affects:</span>
                      {affectedBiomarkers.map((bio, i) => {
                        const isChipSelected =
                          isOpen &&
                          ((bio.type === "metric" &&
                            contentType === "metric" &&
                            metricData?.id === bio.panelData.id) ||
                            (bio.type === "bloodBiomarker" &&
                              contentType === "bloodBiomarker" &&
                              bloodBiomarkerData?.id === bio.panelData.id));

                        return (
                          <span
                            key={i}
                            style={styles.biomarkerChip(theme, isChipSelected)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (bio.type === "metric") {
                                openPanel("metric", {
                                  metricData: bio.panelData,
                                });
                              } else {
                                openPanel("bloodBiomarker", {
                                  bloodBiomarkerData: bio.panelData,
                                });
                              }
                            }}
                          >
                            {bio.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollableWrapper>
      ) : (
        <div style={styles.emptyServicesState}>
          <span style={styles.emptyServicesMainText(theme)}>
            {`There are no recommended services linked to ${
              currentSystem?.name || "the selected system"
            } based on its biomarkers.`}
          </span>
          <br />
          <span style={styles.emptyServicesSubText(theme)}>
            Please select a different system to view relevant services.
          </span>
        </div>
      )}
      {servicesData && (
        <ServiceViewModal
          open={!!modalService}
          onClose={() => setModalService(null)}
          service={modalService}
          labels={servicesData.labels}
          unitType={servicesData.unitType}
        />
      )}
    </>
  );
};

export default RecommendedServicesTab;

const styles = {
  servicesListContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "10px",
    padding: "16px 6px 16px 16px",
    flex: 1,
  },
  serviceCard: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    flexShrink: 0,
  }),
  serviceCardExpanded: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: "1px solid #1064DB",
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    flexShrink: 0,
    boxShadow: "0px 8px 20px rgba(0,0,0,0.06), 0px 2px 8px rgba(0,0,0,0.04)",
  }),
  serviceCardTopRow: {
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    height: "61px",
  },
  serviceCardImage: {
    width: "121px",
    height: "61px",
    objectFit: "cover" as "cover",
    flexShrink: 0,
    borderTopLeftRadius: "8px",
  },
  serviceCardInfo: {
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    gap: "4px",
    padding: "12px",
    flex: 1,
    minWidth: 0,
  },
  viewDetailsLink: {
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: "#1064DB",
    cursor: "pointer",
    whiteSpace: "nowrap" as "nowrap",
    paddingRight: "12px",
    flexShrink: 0,
  },
  affectsRow: {
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
    gap: "6px",
    backgroundColor: "#F7F9FA",
    padding: "12px",
  },
  affectsLabel: {
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    color: "#6D7075",
  },
  biomarkerChip: (theme: Theme, isSelected?: boolean) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    backgroundColor: "#FFFFFF",
    border: isSelected
      ? `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`
      : "1px solid #E1E3E5",
    borderRadius: "8px",
    padding: "4px 8px",
    cursor: "pointer",
  }),
  serviceCardName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colorTokens.N[90],
    margin: 0,
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  serviceCardType: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "10px",
    lineHeight: "1.4",
    letterSpacing: "-0.2px",
    color: theme.colorTokens.N[80],
    margin: 0,
  }),
  emptyServicesState: {
    margin: "0 auto",
    width: "85%",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "0 16px",
    textAlign: "center" as "center",
    gap: "4px",
  },
  emptyServicesMainText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyServicesSubText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
